import PlatformApiSdk from '@monksflow/platform-api-sdk';
import type { AxiosError, AxiosRequestConfig } from 'axios';
import { z } from 'zod';
import { refreshTokenInterceptor } from '@/lib/api/interceptors/refresh-token.interceptor';
import { ENV } from '@/lib/config/env';

// This module exists just to ensure that the Platform SDK is initialized before any module uses it
// If we consume the SDK through this "alias" we don't need to worry about module resolution order
// or whether it should be initialized or not

PlatformApiSdk.init({
  platformApiUrl: ENV.PLATFORM_API_BASE_URL,
  platformAppUrl: ENV.PLATFORM_BASE_URL,
  withAuthInterceptor: false,
});

// Asks the platform for this environment's session when the browser also carries another
// environment's auth cookies. Safe only because every service Agent Studio calls makes the same
// choice; otherwise the platform would accept a session those services refuse.
Object.assign(PlatformApiSdk.platformApi.defaults.headers.common, {
  'x-mf-resolve-duplicate-cookies': '1',
});

// Attach the same refresh-token interceptor used by the workspace-api client
// so 401s on Platform API calls trigger token refresh + retry
PlatformApiSdk.platformApi.interceptors.response.use(undefined, async (error: AxiosError) => {
  if (error.response?.status === 401 && !ENV.ENABLE_LOCAL_TESTING_AUTHENTICATION) {
    return await refreshTokenInterceptor(error, PlatformApiSdk.platformApi);
  }
  throw error;
});

export const PlatformApi = PlatformApiSdk;

export type PermissionScope = 'workspace' | 'project' | 'org';

// The SDK's axios instance defaults to `Accept: application/json;v=1`;
// overriding it per-request selects the v2 `{ permissions: string[] }` shape.
const v2PermissionsSchema = z.object({ permissions: z.array(z.string()).nullish() });

async function getV2Permissions(
  path: string,
  config?: Pick<AxiosRequestConfig, 'params' | 'timeout'>,
) {
  const { data } = await PlatformApiSdk.platformApi.get<unknown>(path, {
    ...config,
    headers: { Accept: 'application/json;v=2' },
  });
  return v2PermissionsSchema.parse(data).permissions ?? [];
}

// The caller's effective permissions scoped to a single resource (mirrors the
// backend's buildScopedPermissionsResolver).
export const getScopedPermissions = (type: PermissionScope, identifier: string) =>
  getV2Permissions('/auth/permissions', { params: { type, identifier } });

// Agent-studio keys are served here, not in the access token, to keep its cookie under 4 KB.
// The SDK sets no timeout, so the caller passes one: app bootstrap awaits this call.
export const getAgentStudioPermissions = (timeoutMs: number) =>
  getV2Permissions('/auth/permissions/agent-studio', { timeout: timeoutMs });
